import { Auth_Users, User_Professional_Public } from "@graphql/schema.types";
import { CreateEventMutation } from "@graphql/types";
import { CREATE_EVENT_QUERY } from "@pages/helps/queries";
import { HttpError, useCustomMutation, useGetIdentity, useInvalidate, useTranslation } from "@refinedev/core";
import { roundTime } from "@utils/time";
import { APPOINTMENT_TIME_MINUTES, APP_NAME, EventCode, getMetadataString } from "core/calendar/definitions";
import { HELPS_EMERGENCY_TIME_MINUTES } from "core/helps/definitions";
import dayjs from "dayjs";
import { calendar_v3 } from "googleapis";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";

export function useEvent() {
    const { mutateAsync: createEvent, ...rest } = useCustomMutation<CreateEventMutation, HttpError, any>()
    const calendarId = useAppCalendar()
    const invalidate = useInvalidate()
    const { translate } = useTranslation()

    const { data: user } = useGetIdentity<Auth_Users>()


    return {
        ...rest,
        createEmergencyEvent: async (professional: User_Professional_Public) => {

            if (!calendarId) return

            const { data: eventRes } = await createEvent({
                url: '',
                method: 'post',
                values: {},
                meta: {
                    gqlMutation: CREATE_EVENT_QUERY,
                    gqlVariables: {
                        professional: {
                            id: professional.id,
                            email: professional.email
                        },
                        event: {
                            summary: `${translate(`calendar-defs.events.${EventCode.EMERGENCY_CALL}`)} - ${APP_NAME}`,
                            description: getMetadataString({
                                code: EventCode.EMERGENCY_CALL
                            }),
                            conferenceData: {
                                createRequest: {
                                    requestId: crypto.randomUUID(),
                                    conferenceSolutionKey: {
                                        type: 'hangoutsMeet'
                                    }
                                },
                            },
                            start: {
                                dateTime: roundTime(dayjs()).toISOString(),
                                timeZone: 'America/Bogota' //TODO: change in international
                                // date: dayjs().startOf('day').toISOString()
                            },
                            end: {
                                dateTime: roundTime(dayjs()).add(HELPS_EMERGENCY_TIME_MINUTES, 'minutes').toISOString(),
                                timeZone: 'America/Bogota' //TODO: change in international
                                // date: dayjs().startOf('day').add(1, 'day').toISOString()
                            },
                            source: {
                                title: EventCode.EMERGENCY_CALL,
                                url: 'https://users.hachimind.com'
                            },
                            attendees: [
                                {
                                    email: user?.email,
                                    displayName: user?.display_name
                                }
                            ],
                        } as calendar_v3.Schema$Event
                    }
                }
            })

            await invalidate({
                dataProviderName: 'gapi',
                resource: 'calendar/calendars/:calendar/events',
                invalidates: ['list']
            })

            return eventRes
        },
        createAppointmentEvent: async (startTime: Date | dayjs.Dayjs, professional: User_Professional_Public) => {

            if (!calendarId) return

            const { data: eventRes } = await createEvent({
                url: '',
                method: 'post',
                values: {},
                meta: {
                    gqlMutation: CREATE_EVENT_QUERY,
                    gqlVariables: {
                        professional: {
                            id: professional.id,
                            email: professional.email
                        },
                        event: {
                            summary: `${translate(`calendar-defs.events.${EventCode.APPOINTMENT}`)} - ${APP_NAME}`,
                            description: getMetadataString({
                                code: EventCode.APPOINTMENT
                            }),
                            conferenceData: {
                                createRequest: {
                                    requestId: crypto.randomUUID(),
                                    conferenceSolutionKey: {
                                        type: 'hangoutsMeet'
                                    }
                                },
                            },
                            start: {
                                dateTime: roundTime(dayjs(startTime)).toISOString(),
                                timeZone: 'America/Bogota' //TODO: change in international
                                // date: dayjs().startOf('day').toISOString()
                            },
                            end: {
                                dateTime: roundTime(dayjs(startTime)).add(APPOINTMENT_TIME_MINUTES, 'minutes').toISOString(),
                                timeZone: 'America/Bogota' //TODO: change in international
                                // date: dayjs().startOf('day').add(1, 'day').toISOString()
                            },
                            source: {
                                title: EventCode.APPOINTMENT,
                                url: 'https://users.hachimind.com'
                            },
                            attendees: [
                                {
                                    email: user?.email,
                                    displayName: user?.display_name
                                }
                            ],
                        } as calendar_v3.Schema$Event
                    }
                }
            })

            await invalidate({
                dataProviderName: 'gapi',
                resource: 'calendar/calendars/:calendar/events',
                invalidates: ['list']
            })

            return eventRes
        },
        redirectToMeet: async (eventResult: CreateEventMutation) => {
            // logger.debug(eventResult)
            // invalidates to show 'already in call' popup
            // redirect to meet call
            const meetUrl = eventResult?.create_event?.event.meet.url
            if (!!meetUrl)
                window.open(meetUrl, '_blank');
        }
    }
}